// client/src/contexts/PointContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';

const API_URL = process.env.REACT_APP_API_URL;
const PointContext = createContext();

// sessionStorage 캐시 키
const CACHE_KEY_PREFIX = 'user_points_cache_';
const CACHE_DURATION = 10000; // 10초

// 캐시에서 포인트 가져오기
const getCachedPoints = (userRole) => {
  try {
    const cacheKey = CACHE_KEY_PREFIX + userRole;
    const cached = sessionStorage.getItem(cacheKey);
    if (!cached) return null;
    
    const { points, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp > CACHE_DURATION) {
      sessionStorage.removeItem(cacheKey);
      return null;
    }
    
    return points;
  } catch {
    return null;
  }
};

// 캐시에 포인트 저장
const setCachedPoints = (userRole, points) => {
  try {
    const cacheKey = CACHE_KEY_PREFIX + userRole;
    sessionStorage.setItem(cacheKey, JSON.stringify({
      points,
      timestamp: Date.now()
    }));
  } catch {
    // sessionStorage 실패 시 무시
  }
};

export function PointProvider({ children, userRole }) {
  // ✅ useMemo로 캐시 읽기 최적화
  const cachedData = useMemo(() => getCachedPoints(userRole), [userRole]);
  
  // ✅ null-safe 연산자 사용 (포인트가 0일 때 버그 수정)
  const [points, setPoints] = useState(cachedData ?? 0);
  const [loading, setLoading] = useState(cachedData === null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const pollingIntervalRef = useRef(null);
  const isMountedRef = useRef(true); // ✅ 메모리 누수 방지

  // ✅ userRole 변경 시 캐시 재확인
  useEffect(() => {
    const cached = getCachedPoints(userRole);
    if (cached !== null) {
      setPoints(cached);
      setLoading(false);
    }
  }, [userRole]);

  // 포인트 조회 함수
  const fetchPoints = useCallback(async (showLoading = false) => {
    if (!userRole || !isMountedRef.current) {
      return;
    }
    
    if (showLoading) {
      setLoading(true);
    }
    
    try {
      const endpoint = userRole === 'marketer' 
        ? '/api/marketer/mybiz/points/balance'
        : '/api/influencer/mybiz/points/balance';
      
      const res = await fetch(`${API_URL}${endpoint}`, {
        credentials: 'include'
      });
      
      if (!res.ok || !isMountedRef.current) {
        throw new Error('Failed to fetch points');
      }
      
      const data = await res.json();
      
      if (data.ok && isMountedRef.current) {
        const newPoints = Number(data.points || 0);
        setPoints(newPoints);
        setCachedPoints(userRole, newPoints);
      }
    } catch (error) {
      console.error('포인트 조회 오류:', error);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        setIsRefreshing(false);
      }
    }
  }, [userRole]);

  // ✅ 즉시 갱신 (포인트 충전/사용 후 호출용)
  const refreshPoints = useCallback(async () => {
    // 캐시 무효화
    try {
      const cacheKey = CACHE_KEY_PREFIX + userRole;
      sessionStorage.removeItem(cacheKey);
    } catch {}
    
    // ✅ 이벤트 발생 시에는 isRefreshing 상태 사용 (깜박임 대신 시각적 효과)
    setIsRefreshing(true);
    await fetchPoints(false); // loading은 false로 유지
  }, [fetchPoints, userRole]);

  // 초기 로드
  useEffect(() => {
    fetchPoints(true); // 초기 로드만 loading 표시
  }, [fetchPoints]);

  // 백그라운드 폴링 (30초) - ✅ 그대로 유지
  useEffect(() => {
    if (!userRole) return;

    pollingIntervalRef.current = setInterval(() => {
      fetchPoints(false); // loading 없이 조용히 업데이트
    }, 30000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [fetchPoints, userRole]);

  // 포커스 복귀 시 갱신 - loading 없이 업데이트
  useEffect(() => {
    const handleFocus = () => {
      fetchPoints(false);
    };
    
    window.addEventListener('focus', handleFocus);
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [fetchPoints]);

  // 이벤트 기반 갱신 리스너
  useEffect(() => {
    const handlePointsChanged = () => {
      refreshPoints();
    };
    
    window.addEventListener('points-changed', handlePointsChanged);
    return () => {
      window.removeEventListener('points-changed', handlePointsChanged);
    };
  }, [refreshPoints]);

  // ✅ Cleanup (메모리 누수 방지)
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  return (
    <PointContext.Provider value={{ points, loading, isRefreshing, refreshPoints }}>
      {children}
    </PointContext.Provider>
  );
}

export const usePoints = () => {
  const context = useContext(PointContext);
  if (context === undefined) {
    throw new Error('usePoints must be used within a PointProvider');
  }
  return context;
};

